import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const PlaceInput = z.object({
  label: z.string().trim().min(1).max(60),
  location: z.string().trim().min(1).max(80),
  address: z.string().trim().max(160).optional().or(z.literal("")),
  isDefault: z.boolean().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});

export const listSavedPlaces = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const cols = "id, label, address, location, is_default, latitude, longitude";
    const { data, error } = await context.supabase
      .from("saved_places")
      .select(cols)
      .order("is_default", { ascending: false })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);

    const rows = data ?? [];
    const missing = rows.filter((r) => r.latitude == null || r.longitude == null);
    if (missing.length === 0) return rows;

    // Backfyll koordinater för adresser som sparades innan geokodningen fanns.
    const { geocodeLocation } = await import("./ads.server");
    for (const row of missing) {
      const point = await geocodeLocation(row.location, row.address || undefined);
      if (!point) continue;
      row.latitude = point.latitude;
      row.longitude = point.longitude;
      await context.supabase
        .from("saved_places")
        .update({ latitude: point.latitude, longitude: point.longitude })
        .eq("id", row.id);
    }
    return rows;
  });

export const saveSavedPlace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => PlaceInput.parse(input))
  .handler(async ({ data, context }) => {
    // Hämta befintliga adresser: samma namn eller samma ort+adress räknas som samma plats.
    const { data: mine } = await context.supabase
      .from("saved_places")
      .select("id, label, location, address")
      .eq("user_id", context.userId);

    const all = mine ?? [];
    const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
    const existing =
      all.find((p) => norm(p.label) === norm(data.label)) ??
      all.find(
        (p) => norm(p.location) === norm(data.location) && norm(p.address) === norm(data.address),
      );

    // Första sparade adressen blir automatiskt hemadress.
    const makeDefault = (data.isDefault ?? false) || all.length === 0;

    let point: { latitude: number; longitude: number } | null =
      data.latitude != null && data.longitude != null
        ? { latitude: data.latitude, longitude: data.longitude }
        : null;
    if (!point) {
      const { geocodeLocation } = await import("./ads.server");
      point = await geocodeLocation(data.location, data.address || undefined);
    }

    const row = {
      user_id: context.userId,
      label: data.label,
      location: data.location,
      address: data.address ?? "",
      is_default: makeDefault,
      latitude: point?.latitude ?? null,
      longitude: point?.longitude ?? null,
    };

    if (makeDefault) {
      await context.supabase
        .from("saved_places")
        .update({ is_default: false })
        .eq("user_id", context.userId);
    }

    const cols = "id, label, address, location, is_default, latitude, longitude";
    const { data: saved, error } = existing
      ? await context.supabase
          .from("saved_places")
          .update(row)
          .eq("id", existing.id)
          .select(cols)
          .single()
      : await context.supabase.from("saved_places").insert(row).select(cols).single();

    if (error) throw new Error(`Kunde inte spara adressen: ${error.message}`);
    return saved;
  });

export const deleteSavedPlace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("saved_places").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Väljer vilken sparad adress som är hemadress för "nära mig". */
export const setDefaultSavedPlace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await context.supabase
      .from("saved_places")
      .update({ is_default: false })
      .eq("user_id", context.userId);
    const { error } = await context.supabase
      .from("saved_places")
      .update({ is_default: true })
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Adressförslag från OpenStreetMap, begränsat till Sverige. */
export const searchAddresses = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ query: z.string().trim().min(3).max(120) }).parse(input),
  )
  .handler(async ({ data }) => {
    try {
      const url =
        "https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&countrycodes=se&q=" +
        encodeURIComponent(data.query);
      const res = await fetch(url, {
        headers: { "User-Agent": "Saljavo/1.0 (adresser)", "Accept-Language": "sv" },
      });
      if (!res.ok) return [];
      const json = (await res.json()) as Array<{
        display_name: string;
        lat: string;
        lon: string;
        address?: Record<string, string>;
      }>;
      return json.map((hit) => {
        const a = hit.address ?? {};
        const street = [a.road, a.house_number].filter(Boolean).join(" ");
        const city = a.city || a.town || a.village || a.hamlet || a.municipality || "";
        return {
          label: hit.display_name,
          street,
          city,
          latitude: Number(hit.lat),
          longitude: Number(hit.lon),
        };
      });
    } catch {
      return [];
    }
  });
